import { useInfiniteQuery } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { Link } from "react-router";
import { Plus } from "lucide-react";

import { listCampaigns } from "@/api/messaging";
import { CampaignStatusBadge } from "@/components/messaging/badges";
import { RefreshFailed, loadFailure } from "@/components/Spinner";
import { ButtonLink, EmptyState, Page, PageHeader, StatusPill, Table, type TableColumn } from "@/components/ui";
import { formatDateTime } from "@/lib/dates";
import { MESSAGING } from "@/lib/sections";
import type { MessagingCampaign } from "@/types/api";

const COLUMNS: TableColumn<MessagingCampaign>[] = [
  {
    key: "name",
    header: "Name",
    cell: (c) => (
      <>
        <Link to={`/messaging/campaigns/${c.id}`} className="text-brand-700 hover:underline">
          {c.name}
        </Link>
        {c.requiresProductionKey && (
          <StatusPill tone="danger" dot={false} className="ml-2">
            Production
          </StatusPill>
        )}
      </>
    ),
  },
  { key: "template", header: "Template", mono: true, cell: (c) => c.templateSlug },
  { key: "status", header: "Status", cell: (c) => <CampaignStatusBadge status={c.status} /> },
  {
    key: "recipients",
    header: "Recipients",
    cell: (c) => (
      <>
        {c.counts.resolved}
        {c.counts.sent > 0 && <span className="text-ink-muted"> · {c.counts.sent} sent</span>}
        {c.counts.failed > 0 && <span className="text-danger-fg"> · {c.counts.failed} failed</span>}
      </>
    ),
  },
  {
    key: "created",
    header: "Created",
    mono: true,
    nowrap: true,
    cell: (c) => (
      <>
        {formatDateTime(c.createdAt)} <span className="text-ink-muted">by {c.createdBy}</span>
      </>
    ),
  },
];

export function MessagingCampaignsPage() {
  const auth = useAuth();
  const token = auth.user?.id_token ?? "";
  const campaigns = useInfiniteQuery({
    queryKey: ["messaging", "campaigns"],
    queryFn: ({ pageParam }) => listCampaigns(token, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled: !!token,
    refetchInterval: (query) =>
      query.state.data?.pages.some((p) => p.items.some((c) => c.status === "resolving" || c.status === "sending")) ? 5_000 : false,
  });
  const items = campaigns.data?.pages.flatMap((p) => p.items);

  return (
    <Page>
      <PageHeader
        section={MESSAGING}
        title="Campaigns"
        subtitle="A campaign is a template sent to an audience computed at send time. Resolve the audience, read the list, then send."
        actions={
          <ButtonLink to="/messaging/campaigns/new" variant="primary" icon={Plus}>
            New campaign
          </ButtonLink>
        }
      />

      <RefreshFailed query={campaigns} className="mb-4" />
      <Table
        label="Campaigns"
        columns={COLUMNS}
        rows={items}
        rowKey={(c) => c.id}
        error={loadFailure(campaigns)}
        more={
          campaigns.hasNextPage
            ? { loading: campaigns.isFetchingNextPage, onLoadMore: () => void campaigns.fetchNextPage() }
            : undefined
        }
        empty={
          <EmptyState
            title="No campaigns yet"
            action={
              <ButtonLink to="/messaging/campaigns/new" variant="primary" icon={Plus}>
                New campaign
              </ButtonLink>
            }
          >
            Create one to send a template to an audience.
          </EmptyState>
        }
      />
    </Page>
  );
}
